import type { PropertyDeclarationMap } from '@videojs/element';
import { GoogleCastExtension as GoogleCastExtensionBase, type GoogleCastExtensionProps } from '@videojs/google-cast';

import { MediaExtensionElement } from '../media-extension-element';

/**
 * Adds the Google Cast extension to the surrounding player's media.
 *
 * Renders nothing — place it inside the player as a sibling of the media element and it follows the active media.
 *
 * @example
 *   ```html
 *   <video-player>
 *   <hlsjs-video src="https://example.com/stream.m3u8"></hlsjs-video>
 *   <google-cast receiver="YOUR_APP_ID"></google-cast>
 *   </video-player>
 *   ```;
 */
export class GoogleCastExtension extends MediaExtensionElement<GoogleCastExtensionBase> {
  static readonly tagName = 'google-cast';

  static override properties = {
    src: { type: String },
    contentType: { type: String, attribute: 'content-type' },
    streamType: { type: String, attribute: 'stream-type' },
    receiver: { type: String },
    // `customData` takes an object, so it's a property-only prop.
  } satisfies PropertyDeclarationMap<Exclude<keyof GoogleCastExtensionProps, 'customData'>>;

  protected createComponent(): GoogleCastExtensionBase {
    return new GoogleCastExtensionBase();
  }

  /** Source URL loaded on the Cast receiver. Falls back to the media's `src` / `currentSrc`. */
  get src(): string {
    return this.component.src ?? '';
  }

  set src(value: string | null | undefined) {
    this.component.src = value ?? undefined;
  }

  /** MIME type of the Cast source. When unset, the receiver infers it from the URL. */
  get contentType(): string | undefined {
    return this.component.contentType;
  }

  set contentType(value: string | null | undefined) {
    this.component.contentType = value ?? undefined;
  }

  /** Stream type used on the Cast receiver. Falls back to the media's `streamType`. */
  get streamType(): GoogleCastExtensionProps['streamType'] {
    return this.component.streamType;
  }

  set streamType(value: GoogleCastExtensionProps['streamType'] | null) {
    this.component.streamType = value ?? undefined;
  }

  /** Cast receiver application ID. Defaults to Google's default media receiver. */
  get receiver(): string | undefined {
    return this.component.receiver;
  }

  set receiver(value: string | null | undefined) {
    this.component.receiver = value ?? undefined;
  }

  /** Custom data sent to the Cast receiver with the load request. */
  get customData(): GoogleCastExtensionProps['customData'] {
    return this.component.customData;
  }

  set customData(value: GoogleCastExtensionProps['customData']) {
    this.component.customData = value;
  }
}
